<template>
  <LazyDrawer
    position="right"
    :is-open="qtyCalculatorTray"
    :hide-scroll="false"
    class="text-charcoal-default"
    data-test-id="qtyCalcTray"
  >
    <template #header>
      <Stack tag="div" align="start" justify="end">
        <button @click="closeTray('X button')" class="justify-self-end" data-test-id="qtyCalcTray-closeBtn">
          <Icon name="plus-thick" filled class="rotate-45" :size="16" />
        </button>
      </Stack>
      <Heading tag="h3" size="xs" class="mt-xs">Quantity Calculator</Heading>
    </template>

    <Stack direction="row" gap="xs" align="center" class="p-sm border-grey-default border-b-sm">
      <img :src="imageSrc" class="w-xl h-xl" data-test-id="qtyCalcTray-productImage" />
      <Text size="sm" weight="semi" data-test-id="qtyCalcTray-productName"> {{ product.name }}</Text>
    </Stack>

    <aside class="p-sm px-xs md:px-sm">
      <Stack direction="col">
        <section v-for="(area, index) in areasToCover" :key="index">
          <Text weight="bold" class="mb-xs"> Area {{ index + 1 }} </Text>
          <section gap="xs" class="grid grid-cols-11 md:grid-cols-8 gap-2xs md:gap-xs items-end">
            <Input
              v-model="areasToCover[index].len"
              @change:input-value="handleInput($event)"
              :id="`area-${index}-len`"
              :data-test-id="`area-${index}-len`"
              data-name="len"
              placeholder="0"
              label="Length (m)"
              type="number"
              class="col-span-4 md:col-span-3 no-arrows"
            />

            <Input
              v-model="areasToCover[index].width"
              @change:input-value="handleInput($event)"
              :id="`area-${index}-width`"
              :data-test-id="`area-${index}-width`"
              data-name="width"
              placeholder="0"
              label="Width (m)"
              type="number"
              class="col-span-4 md:col-span-3 no-arrows"
            />

            <Stack gap="2xs" align="center" class="mb-[14px] h-sm col-span-3 md:col-span-2">
              <Text
                weight="bold"
                :class="['text-center w-xl md:w-2xl', area.width * area.len ? 'visible' : 'invisible']"
                :data-test-id="`area-${index}`"
              >
                {{ toArea(area) }}
              </Text>
              <Icon
                v-if="index"
                name="trash"
                :size="16"
                class="text-red-dark cursor-pointer w-sm"
                :data-test-id="`deleteArea-${index}`"
                @click="deleteArea(index)"
              />
            </Stack>
          </section>
        </section>

        <Stack
          tag="button"
          gap="2xs"
          class="text-mkm-blue-light cursor-pointer"
          align="center"
          data-test-id="addAreaBtn"
          @click="addNewArea"
        >
          <Icon name="plus-thick" :size="16" />
          <Text weight="bold"> Add area </Text>
        </Stack>

        <Checkbox
          id="wastage"
          data-test-id="wastageCheckbox"
          label="Add 10% for wastage"
          :model-value="withWastage"
          @update:model-value="withWastage = $event"
        />

        <Button
          variant="outline"
          size="md"
          class="!w-full mx-auto"
          data-test-id="calculateBtn"
          :disabled="!canCalculate"
          @click="calculateTotalAreaAndCost"
        >
          <Icon name="calculator" :size="16" /> Calculate
        </Button>
      </Stack>
    </aside>

    <template #footer>
      <Stack direction="col" gap="3xs" v-if="isCalculated" class="text-sm font-semi">
        <Stack class="w-full" justify="between">
          <div>Total Area</div>
          <dov class="font-bold" data-test-id="totalArea">
            {{ toArea(totalArea) }}
          </dov>
        </Stack>
        <Stack class="w-full" justify="between">
          <div>No. of tiles required</div>
          <div class="font-bold" data-test-id="requiredQty">
            {{ numOfUnits }}
          </div>
        </Stack>
        <Stack class="w-full" justify="between">
          <div>Total</div>
          <div class="font-bold" data-test-id="totalCost">
            {{ toCurrency({ value: total }) }}
          </div>
        </Stack>

        <Text weight="bold" class-name="!font-italic" size="xs" align="right" class="italic mt-[14px] mb-[18px]">
          * Price may differ slightly when added to basket
        </Text>
      </Stack>
      <Stack align="center" justify="between" class="w-full" gap="2xs">
        <Button
          variant="neutral"
          size="md"
          class="!w-1/2"
          data-test-id="resetQtyBtn"
          :disabled="areasToCover.every((area) => !area.len && !area.width)"
          @click="resetForm"
        >
          Reset
        </Button>
        <Button
          variant="secondary"
          size="md"
          class="!w-1/2"
          data-test-id="updateQtyBtn"
          :disabled="!isCalculated"
          @click="handleQtyUpdate(numOfUnits)"
        >
          Update
        </Button>
      </Stack>
    </template>
  </LazyDrawer>
</template>

<script lang="ts" setup>
import { toCurrency, toArea } from "mkm-avengers";
import { Area } from "~/composables/useQtyTray";
import { IMAGES } from "~/constants/images";

const emit = defineEmits(["update:qty"]);

const props = defineProps<{
  product: any;
  coverage: number;
  price: number;
}>();

const totalArea = ref<number>(0);
const numOfUnits = ref<number>(0);
const total = ref<number>(0);
const emptyArea = { width: 0, len: 0 };
const withWastage = ref<boolean>(false);
const isCalculated = ref<boolean>(false);
const areasToCover = ref<Area[]>([{ ...emptyArea }]);
const startTime = ref();
const endTime = ref();

const { qtyCalculatorTray, setQtyCalculatorTray, isVatIncluded } = useUIState();
const { fireOpenTray, fireCloseTray, fireAreaInputEvent, fireCalculationEvent } = useQtyTray({
  product: props.product,
  withWastage: withWastage.value,
  price: props.price,
  startTime,
  endTime,
  isCalculated,
});

const images = props.product.images;
const imageSrc = images?.length ? images[images.length - 1].url_thumbnail : IMAGES.PLACEHOLDER;

const canCalculate = computed(() => areasToCover.value.every((a) => a.len && a.width));

const addNewArea = () => (areasToCover.value = [...areasToCover.value, { ...emptyArea }]);

const deleteArea = (areaIndx: number) => (areasToCover.value = areasToCover.value.filter((_, idx) => idx !== areaIndx));

const resetForm = () => (areasToCover.value = areasToCover.value.map(() => ({ ...emptyArea })));

const handleInput = (event: any) => {
  const [, index, prop] = event.target.id.split("-") as [string, number, keyof Area];

  const area = areasToCover.value?.[index];

  if (area.width && area.len) fireAreaInputEvent(area);

  areasToCover.value[index][prop] = Math.max(event.target.value, -event.target.value);
};

const closeTray = (method: string = "background click") => {
  fireCloseTray(method);

  if (method !== "background click") setQtyCalculatorTray();
};

const scrollToDetailsSection = () => {
  const productDetailsElement = document.querySelector('[data-test-id="mobile-productDetailContent"]');
  if (productDetailsElement) productDetailsElement.scrollIntoView({ behavior: "smooth" });
};

const handleQtyUpdate = (qty: number) => {
  emit("update:qty", qty);
  closeTray("updatingQty");
  scrollToDetailsSection();
};

const calculateTotalAreaAndCost = () => {
  totalArea.value =
    areasToCover.value
      .map((area) => Number(area.len) * Number(area.width))
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0) *
    (1 + (withWastage.value ? 0.1 : 0));

  numOfUnits.value = Math.ceil(totalArea.value / props.coverage);
  total.value = numOfUnits.value * props.price * (isVatIncluded.value ? 1.2 : 1);
  isCalculated.value = true;

  scrollToDetailsSection();

  fireCalculationEvent({
    numOfUnits: numOfUnits.value,
    total: total.value,
  });
};

watch(qtyCalculatorTray, (isOpen) => {
  if (isOpen) {
    startTime.value = Date.now();
    fireOpenTray();
  } else {
    endTime.value = Date.now();
    closeTray();
  }
  isCalculated.value = false;
});
</script>
